import { supabase } from '@/lib/supabase';
import { useState, useEffect, useCallback } from 'react';

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedPlan?: 'familiar' | 'plus';
}

type PaymentMethod = 'card' | 'store' | '';
type PlanType = 'familiar' | 'plus' | '';
type Step = 'form' | 'loading' | 'store-instructions';

const CURP_REGEX = /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z0-9]\d$/;

function validateCURP(curp: string): boolean {
  return CURP_REGEX.test(curp.toUpperCase());
}

function validatePhone(phone: string): boolean {
  const digits = phone.replace(/\D/g, '');
  return digits.length === 10;
}

export default function RegistrationModal({ isOpen, onClose, preselectedPlan }: RegistrationModalProps) {
  const [step, setStep] = useState<Step>('form');
  const [nombre, setNombre] = useState('');
  const [curp, setCurp] = useState('');
  const [telefono, setTelefono] = useState('');
  const [selectedPlan, setSelectedPlan] = useState<PlanType>(preselectedPlan || '');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvv, setCardCvv] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);

  const [animState, setAnimState] = useState<'closed' | 'entering' | 'visible' | 'leaving'>('closed');

  useEffect(() => {
    if (isOpen) {
      setAnimState('entering');
      const t = setTimeout(() => setAnimState('visible'), 10);
      return () => clearTimeout(t);
    } else {
      if (animState === 'visible' || animState === 'entering') {
        setAnimState('leaving');
        const t = setTimeout(() => {
          setAnimState('closed');
          setStep('form');
          setNombre('');
          setCurp('');
          setTelefono('');
          setSelectedPlan(preselectedPlan || '');
          setPaymentMethod('');
          setTermsAccepted(false);
          setTouched({});
          setSubmitting(false);
        }, 280);
        return () => clearTimeout(t);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleClose = useCallback(() => { onClose(); }, [onClose]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    if (isOpen) window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, handleClose]);

  useEffect(() => {
    if (preselectedPlan) {
      setSelectedPlan(preselectedPlan);
    }
  }, [preselectedPlan]);

  const errors = {
    nombre: nombre.trim().length < 3 ? 'Ingresa tu nombre completo' : '',
    curp: !validateCURP(curp) ? 'CURP inválida (18 caracteres, formato oficial)' : '',
    telefono: !validatePhone(telefono) ? 'Ingresa un número de 10 dígitos' : '',
    plan: !selectedPlan ? 'Selecciona un plan' : '',
    paymentMethod: !paymentMethod ? 'Selecciona un método de pago' : '',
    terms: !termsAccepted ? 'Debes aceptar los términos' : '',
  };

  const isFormValid =
    !errors.nombre &&
    !errors.curp &&
    !errors.telefono &&
    selectedPlan !== '' &&
    paymentMethod !== '' &&
    termsAccepted;

  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(\d{4})/g, '$1 ').trim();
  };

  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length <= 2) return digits;
    return digits.slice(0, 2) + '/' + digits.slice(2);
  };

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length <= 2) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 10);
    setTelefono(raw);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ nombre: true, curp: true, telefono: true, plan: true, paymentMethod: true, terms: true });
    if (!isFormValid) return;

    setSubmitting(true);
    setStep('loading');

    const formData = new URLSearchParams();
    formData.append('nombre', nombre.trim());
    formData.append('curp', curp.toUpperCase().trim());
    formData.append('telefono', telefono);
    formData.append('plan', selectedPlan === 'familiar' ? 'Coverly Basico - $289/mes' : 'Coverly Prime - $399/mes');
    formData.append('metodo_pago', paymentMethod === 'card' ? 'Tarjeta' : 'Pago en tienda (OXXO u otros)');
    formData.append('terminos_aceptados', 'Sí');

    try {
      await fetch('https://readdy.ai/api/form/d7pribaj1g8i0g4g4ke0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
    } catch (_) {
      // continue regardless
    }

    await new Promise((r) => setTimeout(r, 1400));

    if (paymentMethod === 'card') {
      window.open('https://wa.me/526675680042?text=Hola%2C%20quiero%20pagar%20con%20tarjeta%20mi%20membres%C3%ADa%20Coverly', '_blank');
      handleClose();
    } else {
      setStep('store-instructions');
    }

    setSubmitting(false);
  };

  if (animState === 'closed') return null;

  const isLeaving = animState === 'leaving';
  const isEntering = animState === 'entering';

  const overlayStyle: React.CSSProperties = {
    backdropFilter: 'blur(4px)',
    background: 'rgba(10,5,30,0.72)',
    opacity: isEntering || isLeaving ? 0 : 1,
    transition: 'opacity 280ms cubic-bezier(0.22, 1, 0.36, 1)',
  };

  const cardStyle: React.CSSProperties = {
    boxShadow: '0 24px 64px rgba(83,74,183,0.22), 0 4px 24px rgba(10,5,30,0.18)',
    opacity: isEntering || isLeaving ? 0 : 1,
    transform: isEntering
      ? 'scale(0.93) translateY(18px)'
      : isLeaving
      ? 'scale(0.95) translateY(12px)'
      : 'scale(1) translateY(0)',
    transition: isLeaving
      ? 'opacity 240ms ease-in, transform 240ms ease-in'
      : 'opacity 320ms cubic-bezier(0.22, 1, 0.36, 1), transform 320ms cubic-bezier(0.22, 1, 0.36, 1)',
  };

  const planInfo = selectedPlan === 'familiar'
    ? { name: 'Coverly Basico', price: '$289', color: '#534AB7', perDay: '$9.63', people: 'Titular + 2 personas registradas' }
    : selectedPlan === 'plus'
    ? { name: 'Coverly Prime', price: '$399', color: '#0A0A0A', perDay: '$13.30', people: 'Titular + 3 personas registradas' }
    : null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={overlayStyle}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className="relative bg-white rounded-2xl w-full max-w-[520px] max-h-[92vh] overflow-y-auto flex flex-col"
        style={cardStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-[#F3F2FF] hover:bg-[#E8E6FF] text-[#534AB7] transition-colors cursor-pointer"
          aria-label="Cerrar"
        >
          <i className="ri-close-line text-lg leading-none"></i>
        </button>

        {/* STEP: FORM */}
        {(step === 'form' || step === 'loading') && (
          <form
            data-readdy-form
            id="registro-membresia-coverly"
            onSubmit={handleSubmit}
            className="flex flex-col"
            noValidate
          >
            {/* Header */}
            <div className="px-6 md:px-8 pt-7 pb-5 border-b border-[#F0EFFE]">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#534AB7]">
                  <i className="ri-shield-star-fill text-white text-sm leading-none"></i>
                </span>
                <span className="text-[#534AB7] text-xs font-semibold tracking-widest uppercase">Coverly</span>
              </div>
              <h2 className="font-title text-[#0A0A0A] text-xl md:text-2xl font-bold leading-tight mb-2">
                Activa tu membresía
              </h2>
              <p className="text-[#0A0A0A]/55 text-sm leading-relaxed">
                Al completar tu registro recibirás un WhatsApp con tu folio de miembro y el link de acceso a tu app: app.coverly.com.mx
              </p>
            </div>

            {/* Body */}
            <div className="px-6 md:px-8 py-6 flex flex-col gap-5">

              {/* Resumen del plan seleccionado (si viene preseleccionado) */}
              {preselectedPlan && planInfo && (
                <div
                  className="rounded-xl border-2 p-4 flex flex-col gap-2"
                  style={{
                    borderColor: planInfo.color,
                    background: `${planInfo.color}08`,
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-7 h-7 flex items-center justify-center rounded-full"
                        style={{ background: planInfo.color }}
                      >
                        <i className="ri-check-line text-white text-xs leading-none"></i>
                      </span>
                      <span className="text-sm font-bold" style={{ color: planInfo.color }}>
                        {planInfo.name}
                      </span>
                    </div>
                    <span className="font-title text-xl font-bold leading-none" style={{ color: planInfo.color }}>
                      {planInfo.price}<span className="text-xs font-normal text-[#0A0A0A]/50">/mes</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#0A0A0A]/50">
                    <span>Desde {planInfo.perDay} al día</span>
                    <span>?</span>
                    <span>{planInfo.people}</span>
                  </div>
                </div>
              )}

              {/* Nombre */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[#0A0A0A] text-sm font-semibold" htmlFor="reg-nombre">
                  Nombre completo <span className="text-red-500">*</span>
                </label>
                <input
                  id="reg-nombre"
                  name="nombre"
                  type="text"
                  autoComplete="name"
                  placeholder="Ej. María González López"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, nombre: true }))}
                  className={`w-full min-h-[48px] px-4 py-3 rounded-xl border text-sm text-[#0A0A0A] placeholder-[#0A0A0A]/30 outline-none transition-all duration-200 ${
                    touched.nombre && errors.nombre
                      ? 'border-red-400 bg-red-50 focus:border-red-500'
                      : 'border-[#E5E3F7] bg-[#FAFAFE] focus:border-[#534AB7] focus:bg-white'
                  }`}
                />
                {touched.nombre && errors.nombre && (
                  <p className="text-red-500 text-xs flex items-center gap-1">
                    <i className="ri-error-warning-line"></i> {errors.nombre}
                  </p>
                )}
              </div>

              {/* CURP */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[#0A0A0A] text-sm font-semibold" htmlFor="reg-curp">
                  CURP <span className="text-red-500">*</span>
                </label>
                <input
                  id="reg-curp"
                  name="curp"
                  type="text"
                  autoComplete="off"
                  placeholder="Ej. GOML900101HDFNRR09"
                  value={curp}
                  onChange={(e) => setCurp(e.target.value.toUpperCase())}
                  onBlur={() => setTouched((t) => ({ ...t, curp: true }))}
                  maxLength={18}
                  className={`w-full min-h-[48px] px-4 py-3 rounded-xl border text-sm text-[#0A0A0A] placeholder-[#0A0A0A]/30 outline-none transition-all duration-200 font-mono tracking-wider ${
                    touched.curp && errors.curp
                      ? 'border-red-400 bg-red-50 focus:border-red-500'
                      : 'border-[#E5E3F7] bg-[#FAFAFE] focus:border-[#534AB7] focus:bg-white'
                  }`}
                />
                {touched.curp && errors.curp && (
                  <p className="text-red-500 text-xs flex items-center gap-1">
                    <i className="ri-error-warning-line"></i> {errors.curp}
                  </p>
                )}
                <p className="text-[#0A0A0A]/40 text-xs">18 caracteres. Puedes consultarla en renapo.gob.mx</p>
              </div>

              {/* Teléfono */}
              <div className="flex flex-col gap-1.5">
                <label className="text-[#0A0A0A] text-sm font-semibold" htmlFor="reg-telefono">
                  Número de teléfono <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#0A0A0A]/40 text-sm font-medium select-none">+52</span>
                  <input
                    id="reg-telefono"
                    name="telefono"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    placeholder="(55) 1234-5678"
                    value={formatPhone(telefono)}
                    onChange={handlePhoneChange}
                    onBlur={() => setTouched((t) => ({ ...t, telefono: true }))}
                    className={`w-full min-h-[48px] pl-12 pr-4 py-3 rounded-xl border text-sm text-[#0A0A0A] placeholder-[#0A0A0A]/30 outline-none transition-all duration-200 ${
                      touched.telefono && errors.telefono
                        ? 'border-red-400 bg-red-50 focus:border-red-500'
                        : 'border-[#E5E3F7] bg-[#FAFAFE] focus:border-[#534AB7] focus:bg-white'
                    }`}
                  />
                </div>
                {touched.telefono && errors.telefono && (
                  <p className="text-red-500 text-xs flex items-center gap-1">
                    <i className="ri-error-warning-line"></i> {errors.telefono}
                  </p>
                )}
              </div>

              {/* Selector de plan (solo si no viene preseleccionado) */}
              {!preselectedPlan && (
                <div className="flex flex-col gap-2">
                  <p className="text-[#0A0A0A] text-sm font-semibold">
                    Selecciona tu plan <span className="text-red-500">*</span>
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {/* Plan Familiar */}
                    <button
                      type="button"
                      onClick={() => { setSelectedPlan('familiar'); setTouched((t) => ({ ...t, plan: true })); }}
                      className={`flex flex-col items-center justify-center gap-1.5 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer min-h-[72px] bg-[#534AB7] border-[#534AB7] ${
                        selectedPlan === 'familiar'
                          ? 'ring-2 ring-offset-2 ring-[#534AB7] scale-[1.03]'
                          : 'opacity-70 hover:opacity-90'
                      }`}
                    >
                      <span className="text-lg font-bold leading-none text-white">
                        $289
                        <span className="text-xs font-normal opacity-70">/mes</span>
                      </span>
                      <span className="text-[11px] font-semibold text-white/80">Coverly Basico</span>
                    </button>

                    {/* Plan Plus */}
                    <button
                      type="button"
                      onClick={() => { setSelectedPlan('plus'); setTouched((t) => ({ ...t, plan: true })); }}
                      className={`flex flex-col items-center justify-center gap-1.5 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer min-h-[72px] bg-[#0A0A0A] border-[#0A0A0A] ${
                        selectedPlan === 'plus'
                          ? 'ring-2 ring-offset-2 ring-[#0A0A0A] scale-[1.03]'
                          : 'opacity-70 hover:opacity-90'
                      }`}
                    >
                      <span className="text-lg font-bold leading-none text-white">
                        $399
                        <span className="text-xs font-normal opacity-70">/mes</span>
                      </span>
                      <span className="text-[11px] font-semibold text-white/70">Coverly Prime</span>
                    </button>
                  </div>
                  {touched.plan && errors.plan && (
                    <p className="text-red-500 text-xs flex items-center gap-1">
                      <i className="ri-error-warning-line"></i> {errors.plan}
                    </p>
                  )}
                </div>
              )}

              {/* Método de pago */}
              <div className="flex flex-col gap-2">
                <p className="text-[#0A0A0A] text-sm font-semibold">
                  Método de pago <span className="text-red-500">*</span>
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {/* Tarjeta */}
                  <button
                    type="button"
                    onClick={() => { setPaymentMethod('card'); setTouched((t) => ({ ...t, paymentMethod: true })); }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer min-h-[80px] justify-center ${
                      paymentMethod === 'card'
                        ? 'border-[#534AB7] bg-[#F3F2FF]'
                        : 'border-[#E5E3F7] bg-[#FAFAFE] hover:border-[#AFA9EC]'
                    }`}
                  >
                    <span className={`w-9 h-9 flex items-center justify-center rounded-full ${paymentMethod === 'card' ? 'bg-[#534AB7]' : 'bg-[#E5E3F7]'}`}>
                      <i className={`ri-bank-card-line text-lg leading-none ${paymentMethod === 'card' ? 'text-white' : 'text-[#534AB7]'}`}></i>
                    </span>
                    <span className={`text-xs font-semibold ${paymentMethod === 'card' ? 'text-[#534AB7]' : 'text-[#0A0A0A]/60'}`}>Tarjeta</span>
                  </button>

                  {/* Tienda */}
                  <button
                    type="button"
                    onClick={() => { setPaymentMethod('store'); setTouched((t) => ({ ...t, paymentMethod: true })); }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer min-h-[80px] justify-center ${
                      paymentMethod === 'store'
                        ? 'border-[#534AB7] bg-[#F3F2FF]'
                        : 'border-[#E5E3F7] bg-[#FAFAFE] hover:border-[#AFA9EC]'
                    }`}
                  >
                    <span className={`w-9 h-9 flex items-center justify-center rounded-full ${paymentMethod === 'store' ? 'bg-[#534AB7]' : 'bg-[#E5E3F7]'}`}>
                      <i className={`ri-store-2-line text-lg leading-none ${paymentMethod === 'store' ? 'text-white' : 'text-[#534AB7]'}`}></i>
                    </span>
                    <span className={`text-xs font-semibold ${paymentMethod === 'store' ? 'text-[#534AB7]' : 'text-[#0A0A0A]/60'}`}>Pago en tienda</span>
                    <span className={`text-[10px] ${paymentMethod === 'store' ? 'text-[#534AB7]/70' : 'text-[#0A0A0A]/35'}`}>OXXO u otros</span>
                  </button>
                </div>
                {touched.paymentMethod && errors.paymentMethod && (
                  <p className="text-red-500 text-xs flex items-center gap-1">
                    <i className="ri-error-warning-line"></i> {errors.paymentMethod}
                  </p>
                )}

                {/* Campos de tarjeta */}
                {paymentMethod === 'card' && (
                  <div className="flex flex-col gap-3 mt-1 p-4 rounded-xl border border-[#C8C4F0] bg-[#F3F2FF]">
                    {/* Número de tarjeta */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[#0A0A0A] text-xs font-semibold">Número de tarjeta</label>
                      <div className="relative">
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="1234 5678 9012 3456"
                          value={formatCardNumber(cardNumber)}
                          onChange={(e) => setCardNumber(e.target.value.replace(/\D/g, '').slice(0, 16))}
                          className="w-full min-h-[44px] px-4 py-3 pr-10 rounded-xl border border-[#E5E3F7] bg-white text-sm text-[#0A0A0A] outline-none focus:border-[#534AB7] placeholder-[#0A0A0A]/30"
                        />
                        <i className="ri-bank-card-line absolute right-3 top-1/2 -translate-y-1/2 text-[#534AB7] text-lg"></i>
                      </div>
                    </div>
                    {/* Nombre en tarjeta */}
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[#0A0A0A] text-xs font-semibold">Nombre en la tarjeta</label>
                      <input
                        type="text"
                        placeholder="Como aparece en tu tarjeta"
                        value={cardName}
                        onChange={(e) => setCardName(e.target.value.toUpperCase())}
                        className="w-full min-h-[44px] px-4 py-3 rounded-xl border border-[#E5E3F7] bg-white text-sm text-[#0A0A0A] outline-none focus:border-[#534AB7] placeholder-[#0A0A0A]/30"
                      />
                    </div>
                    {/* Fecha y CVV */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[#0A0A0A] text-xs font-semibold">Fecha de vencimiento</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="MM/AA"
                          value={cardExpiry}
                          onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                          maxLength={5}
                          className="w-full min-h-[44px] px-4 py-3 rounded-xl border border-[#E5E3F7] bg-white text-sm text-[#0A0A0A] outline-none focus:border-[#534AB7] placeholder-[#0A0A0A]/30"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[#0A0A0A] text-xs font-semibold">CVV</label>
                        <input
                          type="text"
                          inputMode="numeric"
                          placeholder="123"
                          value={cardCvv}
                          onChange={(e) => setCardCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                          maxLength={4}
                          className="w-full min-h-[44px] px-4 py-3 rounded-xl border border-[#E5E3F7] bg-white text-sm text-[#0A0A0A] outline-none focus:border-[#534AB7] placeholder-[#0A0A0A]/30"
                        />
                      </div>
                    </div>
                    <p className="text-[#0A0A0A]/40 text-[11px] flex items-center gap-1">
                      <i className="ri-lock-line"></i> Pago del plan procesado por Conekta - tus datos estan cifrados
                    </p>
                  </div>
                )}

                {/* Bloque informativo - Pago en tienda */}
                {paymentMethod === 'store' && (
                  <div
                    className="mt-1 rounded-xl border border-[#C8C4F0] bg-[#F3F2FF] p-4 flex gap-3"
                    style={{ animation: 'storeBlockIn 300ms cubic-bezier(0.22, 1, 0.36, 1) both' }}
                  >
                    <span className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-[#534AB7]">
                      <i className="ri-ticket-2-line text-white text-lg leading-none"></i>
                    </span>
                    <div className="flex flex-col gap-1.5">
                      <p className="text-[#3C3489] text-sm font-bold leading-snug">
                        Generaremos tu referencia de pago
                      </p>
                      <p className="text-[#0A0A0A]/60 text-xs leading-relaxed">
                        Al continuar, se generará automáticamente una referencia de pago que recibirás por WhatsApp.
                      </p>
                      <p className="text-[#0A0A0A]/60 text-xs leading-relaxed">
                        Con esa referencia podrás pagar en tiendas participantes como OXXO.
                      </p>
                      <p className="text-[#0A0A0A]/60 text-xs leading-relaxed">
                        Después de tu pago, recibirás por WhatsApp el seguimiento para confirmar tu activación y acceder a la app.
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* T?rminos */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    name="terminos_aceptados"
                    checked={termsAccepted}
                    onChange={(e) => { setTermsAccepted(e.target.checked); setTouched((t) => ({ ...t, terms: true })); }}
                    className="sr-only"
                  />
                  <div
                    className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                      termsAccepted ? 'bg-[#534AB7] border-[#534AB7]' : 'bg-white border-[#D0CEEE] group-hover:border-[#534AB7]'
                    }`}
                  >
                    {termsAccepted && <i className="ri-check-line text-white text-xs leading-none"></i>}
                  </div>
                </div>
                <span className="text-[#0A0A0A]/60 text-xs leading-relaxed">
                  Al continuar acepto los{' '}
                  <a
                    href="#terminos"
                    onClick={(e) => e.stopPropagation()}
                    className="text-[#534AB7] underline underline-offset-2 hover:text-[#3C3489]"
                    rel="nofollow"
                  >
                    términos y condiciones
                  </a>{' '}
                  de Coverly.
                </span>
              </label>
              {touched.terms && errors.terms && (
                <p className="text-red-500 text-xs flex items-center gap-1 -mt-3">
                  <i className="ri-error-warning-line"></i> {errors.terms}
                </p>
              )}
            </div>

            {/* Footer sticky */}
            <div className="px-6 md:px-8 pb-7 pt-2 sticky bottom-0 bg-white border-t border-[#F0EFFE]">
              <button
                type="submit"
                disabled={!isFormValid || submitting}
                className={`w-full min-h-[52px] rounded-full font-bold text-sm md:text-base transition-all duration-300 whitespace-nowrap flex items-center justify-center gap-2 ${
                  isFormValid && !submitting
                    ? 'bg-[#534AB7] hover:bg-[#3C3489] text-white hover:scale-[1.02] cursor-pointer'
                    : 'bg-[#E5E3F7] text-[#AFA9EC] cursor-not-allowed'
                }`}
              >
                {step === 'loading' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base leading-none"></i>
                    <span>Procesando...</span>
                  </>
                ) : paymentMethod === 'store' ? (
                  <>
                    <i className="ri-ticket-2-line text-base leading-none"></i>
                    <span>Generar referencia y continuar</span>
                  </>
                ) : (
                  <>
                    <i className="ri-shield-check-line text-base leading-none"></i>
                    <span>Activar membresía</span>
                  </>
                )}
              </button>
              <p className="text-center text-[#0A0A0A]/35 text-[11px] mt-3">
                <i className="ri-lock-line mr-1"></i>Tus datos están protegidos y cifrados
              </p>
            </div>
          </form>
        )}

        {/* STEP: STORE INSTRUCTIONS */}
        {step === 'store-instructions' && (
          <div
            className="flex flex-col px-6 md:px-8 py-8 gap-6"
            style={{ animation: 'storeBlockIn 350ms cubic-bezier(0.22, 1, 0.36, 1) both' }}
          >
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#F3F2FF]">
                <i className="ri-checkbox-circle-fill text-[#534AB7] text-4xl leading-none"></i>
              </div>
              <h2 className="font-title text-[#0A0A0A] text-xl font-bold">¡Registro recibido!</h2>
              <p className="text-[#0A0A0A]/55 text-sm leading-relaxed max-w-sm">
                Hola <strong>{nombre.split(' ')[0]}</strong>, tu solicitud fue registrada. Sigue estos pasos para completar tu pago en tienda:
              </p>
            </div>

            <div className="flex flex-col gap-3">
              {[
                { icon: 'ri-whatsapp-line', color: '#25D366', label: 'Paso 1', text: 'Recibir?s un mensaje de WhatsApp con tu referencia de pago en los próximos minutos.' },
                { icon: 'ri-store-2-line', color: '#534AB7', label: 'Paso 2', text: 'Acude a cualquier OXXO, 7-Eleven, Farmacias del Ahorro u otras tiendas participantes.' },
                { icon: 'ri-barcode-line', color: '#534AB7', label: 'Paso 3', text: 'Muestra tu referencia en caja y realiza el pago. Guarda tu comprobante.' },
                { icon: 'ri-shield-star-fill', color: '#534AB7', label: 'Paso 4', text: 'Al confirmar tu pago, recibirás tu folio de miembro y acceso a la app por WhatsApp.' },
              ].map((s, i) => (
                <div
                  key={s.label}
                  className="flex items-start gap-3 p-3 rounded-xl bg-[#FAFAFE] border border-[#F0EFFE]"
                  style={{ animation: `storeBlockIn 350ms cubic-bezier(0.22, 1, 0.36, 1) ${80 + i * 60}ms both` }}
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0" style={{ background: `${s.color}18` }}>
                    <i className={`${s.icon} text-base leading-none`} style={{ color: s.color }}></i>
                  </span>
                  <div>
                    <p className="text-[#534AB7] text-xs font-bold mb-0.5">{s.label}</p>
                    <p className="text-[#0A0A0A]/65 text-xs leading-relaxed">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={handleClose}
              className="w-full min-h-[52px] bg-[#534AB7] hover:bg-[#3C3489] text-white font-bold rounded-full text-sm transition-all duration-300 hover:scale-[1.02] cursor-pointer whitespace-nowrap"
            >
              Entendido, cerrar
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes storeBlockIn {
          from { opacity: 0; transform: translateY(10px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
      `}</style>
    </div>
  );
}

